import React, { MouseEvent } from 'react';
import { css } from '@emotion/react';
import { useMutation as useMutationT } from '@tanstack/react-query';
import axios from 'axios';
import { Formik, ErrorMessage, Field, FieldProps, useField, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { i18n_enter_your_email_address } from '@yi/i18n/phrases/i18n_enter_your_email_address';
import { i18n_submit } from '@yi/i18n/phrases/i18n_submit';

import { useEmailSchema, analytics, useStorage } from '~/common';
import { getHubspotContext, getHubspotFormApi } from '~/src/common/hubspot';
import { Button } from '~/src/components/buttons';
import { FormInput } from '~/src/components/FormikInputs/elements';
import { useIsSSR } from '~/src/components/hooks/useIsSSR';
import { useSSRSafeFormInputAttributes } from '~/src/components/hooks/useSSRSafeFormInputAttributes';
import { useLocale } from '~/src/components/LocaleProvider';
import { breakpoints, fontWeight } from '~/styles';

export const BUTTON_HEIGHT = '68px';

const styles = {
  form: css({
    display: 'grid',
    flexDirection: 'column',
    alignItems: 'center',
    gridTemplateAreas: " '.' '.'",
    rowGap: '16px',
    [breakpoints.minMobileLandscape]: {
      gridTemplateColumns: 'unset',
      rowGap: '24px',
      justifyItems: 'center',
    },
    [breakpoints.minDesktop]: {
      gridTemplateAreas: " '. .'",
      columnGap: '48px',
      rowGap: 'unset',
      gridTemplateColumns: '1fr 1fr',
      width: '100%',
      alignItems: 'start',
    },
  }),
  input: css({
    width: '100%',
    '.MuiFormLabel-root': {
      fontFamily: 'Montserrat',
      fontStyle: 'normal',
      fontWeight: fontWeight.medium,
      transform: 'translate(32px, 23px) scale(1)',
      position: 'absolute',
      top: '0%',
      margin: '0',
      color: 'var(--Grey-Text, #585858)',
    },
    '.MuiInputLabel-filled.MuiInputLabel-shrink': {
      transform: 'translate(16px, 7px) scale(0.75)',
    },
    '.MuiFormLabel-root:not(.Mui-error)': {
      color: 'var(--Grey-Text, #585858)',
      fontSize: '18px',
      lineHeight: '22px',
      letterSpacing: '0.9px',
    },

    '.MuiFormLabel-asterisk': {
      display: 'none',
    },

    '.MuiInputBase-root': {
      height: BUTTON_HEIGHT,
      borderRadius: '34px',
      border: '1px solid var(--dark-text-54, rgba(88, 88, 88, 0.4))',
      background: '#fff',
      display: 'flex',
      padding: '0',
      alignItems: 'center',
      gap: '16px',
    },

    '.MuiInputBase-root:hover, .MuiFilledInput-root.Mui-focused': {
      background: 'white',
    },

    '.MuiInputBase-root:after': {
      content: 'none',
    },
    '.MuiInputBase-root:before': {
      border: 'none !important',
    },

    '.MuiInputBase-input': {
      height: 'auto',
      color: 'var(--Dark-Text)',
      padding: '28px 17px 17px 24px',
      fontWeight: fontWeight.medium,
      '&:focus': {
        color: 'var(--Dark-Text)',
        fontWeight: fontWeight.medium,
      },
    },
  }),
  button: css({
    button: {
      height: BUTTON_HEIGHT,
      borderRadius: '34px',
      color: 'var(--Pure-White, #FFF)',
      fontFamily: 'Montserrat',
      fontSize: '18px',
      fontStyle: 'normal',
      fontWeight: fontWeight.bold,
      lineHeight: 'normal',
      letterSpacing: '0.9px',
    },
  }),
  formError: css({
    color: 'var(--White)',
  }),
};

const HUBSPOT_FORM_ID_ES = '03dce305-56a4-4c26-b6dc-3839d8637de6';
const HUBSPOT_FORM_ID_EN = '9e26fd86-6dea-4f8e-85a7-dbdb69ca32b2';

interface FormValues {
  email: string;
}

const initialValues: FormValues = {
  email: '',
};

const SubmitButton: React.FC<{ status: ReturnType<typeof useMutationT>['status'] }> = ({
  status,
}) => {
  const [, , { setTouched }] = useField('email');
  const { isSubmitting, isValid, validateField, submitForm } = useFormikContext();

  const onClick = (e: MouseEvent) => {
    e.preventDefault();

    setTouched(true);
    validateField('email');
    submitForm();
  };

  const isSSR = useIsSSR();
  const { locale } = useLocale();
  return (
    <Button
      kind="outlinedWhite"
      fullWidth
      containerCss={styles.button}
      disabled={isSSR || !isValid || isSubmitting || status === 'loading'}
      showSpinner={status === 'loading'}
      type="submit"
      onClick={onClick}
    >
      {i18n_submit(locale)}
    </Button>
  );
};

export const Form: React.FC<{ className?: string; onSubmit: () => void }> = ({
  onSubmit,
  ...props
}) => {
  const { locale } = useLocale();
  const hubspotFormApi = getHubspotFormApi(
    locale === 'es' ? HUBSPOT_FORM_ID_ES : HUBSPOT_FORM_ID_EN,
  );
  const { mutate, status } = useMutationT<{}, {}, { email: string }, {}>({
    mutationFn: ({ email }) => {
      const hubspotData = {
        fields: [
          {
            objectTypeId: '0-1',
            name: 'email',
            value: email,
          },
        ],
        context: getHubspotContext(),
      };
      return axios.post(hubspotFormApi, hubspotData).then(() => {
        onSubmit();
        return { status: 'success' };
      });
    },
  });

  const emailSchema = useEmailSchema();

  const storage = useStorage();

  const validationSchema: Yup.SchemaOf<FormValues> = Yup.object({
    email: emailSchema,
  });

  const isSSR = useIsSSR();
  const emailSSRSafeAtttibutes = useSSRSafeFormInputAttributes('email');

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async values => {
        const email = values.email;
        analytics.sendEmailCaptureEvent(email);
        storage.setEmail(email);
        mutate({ email });
      }}
    >
      {({ handleSubmit }) => (
        <form css={styles.form} onSubmit={handleSubmit} {...props}>
          <Field name="email">
            {({ field, meta }: FieldProps) => (
              <FormInput
                {...field}
                id="footer-email-input"
                label={i18n_enter_your_email_address(locale)}
                variant="filled"
                css={styles.input}
                required
                fullWidth
                disabled={isSSR}
                helperText={
                  meta.error ? (
                    <ErrorMessage
                      name={field.name}
                      render={msg => (
                        <span css={styles.formError} role="alert">
                          {msg}
                        </span>
                      )}
                    />
                  ) : (
                    ''
                  )
                }
                {...emailSSRSafeAtttibutes}
              />
            )}
          </Field>
          <SubmitButton status={status} />
        </form>
      )}
    </Formik>
  );
};
